import React, { useState, useEffect } from 'react';
import theme from '../../../theme/globalTheme';
import { Send, Ship, MapPin, CheckSquare } from 'lucide-react';
import { supabase } from '../../../api/supabase';

const VesselUpgradeEmailSender = () => {
  // Fonction pour obtenir la date de demain au format YYYY-MM-DD
  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  const [formData, setFormData] = useState({
    recipientType: 'client', // 'client' or 'agent'
    departureFrom: 'SBH', // 'SBH', 'SXM', 'AXA'
    destinationTo: 'SXM', // 'SBH', 'SXM', 'AXA'
    clientName: '',
    agentName: '',
    email: '',
    date: getTomorrowDate(), // Date de demain par défaut
    timeType: 'preset', // 'preset' or 'custom'
    presetTime: '09:00', // 9h, 13h, 17h
    customTime: '',
    serviceType: 'Transfer', // 'Transfer' or 'Private Charter'
    numberOfGuests: '2',
    language: 'english',
    isUpgrade: false, // false = confirmation simple, true = surclassement
    newVessel: '' // Nom du nouveau bateau (si upgrade)
  });

  const [emailPreview, setEmailPreview] = useState('');
  const [htmlEmailPreview, setHtmlEmailPreview] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [yachts, setYachts] = useState([]);
  const [loadingYachts, setLoadingYachts] = useState(true);
  const [queuedEmails, setQueuedEmails] = useState([]);
  const [isSendingQueue, setIsSendingQueue] = useState(false);
  const [queueProgress, setQueueProgress] = useState({ sent: 0, total: 0 });

  // Make webhook url constant so batch sending can reuse it
  const MAKE_WEBHOOK_URL = 'https://hook.eu2.make.com/ziechx2b1lutse6wcmrliwag4blynr2r';

  // Helper to build a human-friendly vessel label from yachts data (used in multiple places)
  const getVesselLabel = (value) => {
    if (!value) return '[Unknown Vessel]';
    const found = yachts.find(y => String(y.id) === String(value));
    if (found) {
      const size = found.length_feet ? `${found.length_feet}ft` : (found.length_meters ? `${found.length_meters}m` : '');
      const brandOrName = found.brand || found.name || 'Vessel';
      return size ? `${brandOrName} (${size})` : brandOrName;
    }
    return value;
  };

  // Fetch yachts from database
  useEffect(() => {
    const fetchYachts = async () => {
      try {
        const { data, error } = await supabase
          .from('yachts')
          .select('id, name, brand, length_feet, length_meters, year')
          .order('name');

        if (error) throw error;
        setYachts(data || []);
      } catch (error) {
        console.error('Error fetching yachts:', error);
      } finally {
        setLoadingYachts(false);
      }
    };

    fetchYachts();
  }, []);

  // Fonction pour obtenir l'heure sélectionnée
  const getTime = () => {
    return formData.timeType === 'preset' ? formData.presetTime : formData.customTime;
  };

  // Fonction pour générer la route automatiquement
  const getRoute = () => {
    return `${formData.departureFrom}/${formData.destinationTo}`;
  };

  // Fonction pour savoir si on affiche l'adresse
  const shouldShowAddress = () => {
    // Si c'est un Private Charter, TOUJOURS l'adresse (car forcément à SBH)
    if (formData.serviceType === 'Private Charter') {
      return true;
    }
    // Sinon, seulement si le départ est SBH
    return formData.departureFrom === 'SBH';
  };

  // Templates par type et langue
  const emailTemplates = {
    // CONFIRMATION SIMPLE (isUpgrade = false)
    confirmation_with_address: {
      english: {
        subject: "Booking Confirmation – {date}",
        greeting: "Dear {clientName},",
        intro: "We are pleased to confirm your upcoming booking for tomorrow.",
        meetingPoint: "📍 Meeting Point: 14 Rue Jeanne d'Arc, Gustavia",
        time: "🕒 Time: {time}",
        date: "📅 Date: {date}",
        detailsTitle: "Your booking details:",
        service: "• Service: {serviceType}",
        guests: "• Number of Guests: {numberOfGuests}",
        route: "• Route: {route}",
        team: "Our team will be awaiting your arrival to provide a seamless and elegant boarding experience.",
        childrenNote: "Please confirm if there are children on board.",
        closing: "We look forward to welcoming you tomorrow. Should you need anything prior to departure, please do not hesitate to reach out.",
        signature: "Warm regards,\nMaster Ski Pilou"
      },
      french: {
        subject: "Confirmation de réservation – {date}",
        greeting: "Cher/Chère {clientName},",
        intro: "Nous sommes heureux de confirmer votre réservation pour demain.",
        meetingPoint: "📍 Point de rendez-vous : 14 Rue Jeanne d'Arc, Gustavia",
        time: "🕒 Heure : {time}",
        date: "📅 Date : {date}",
        detailsTitle: "Détails de votre réservation :",
        service: "• Service : {serviceType}",
        guests: "• Nombre de passagers : {numberOfGuests}",
        route: "• Trajet : {route}",
        team: "Notre équipe vous attendra pour vous offrir un embarquement fluide et élégant.",
        childrenNote: "Merci de nous confirmer s'il y a des enfants à bord.",
        closing: "Nous sommes impatients de vous accueillir demain. Si vous avez besoin de quoi que ce soit avant le départ, n'hésitez pas à nous contacter.",
        signature: "Cordialement,\nMaster Ski Pilou"
      }
    },
    confirmation_without_address: {
      english: {
        subject: "Booking Confirmation – {date}",
        greeting: "Dear {clientName},",
        intro: "We are pleased to confirm your upcoming booking for tomorrow.",
        time: "🕒 Time: {time}",
        date: "📅 Date: {date}",
        detailsTitle: "Your booking details:",
        service: "• Service: {serviceType}",
        guests: "• Number of Guests: {numberOfGuests}",
        route: "• Route: {route}",
        team: "Our crew will welcome you directly at the designated departure point for a smooth and effortless embarkation.",
        childrenNote: "Please confirm if there are children on board.",
        closing: "We look forward to welcoming you tomorrow. If you require any further assistance, we remain at your disposal.",
        signature: "Warm regards,\nMaster Ski Pilou"
      },
      french: {
        subject: "Confirmation de réservation – {date}",
        greeting: "Cher/Chère {clientName},",
        intro: "Nous sommes heureux de confirmer votre réservation pour demain.",
        time: "🕒 Heure : {time}",
        date: "📅 Date : {date}",
        detailsTitle: "Détails de votre réservation :",
        service: "• Service : {serviceType}",
        guests: "• Nombre de passagers : {numberOfGuests}",
        route: "• Trajet : {route}",
        team: "Notre équipage vous accueillera directement au point de départ pour un embarquement fluide.",
        childrenNote: "Merci de nous confirmer s'il y a des enfants à bord.",
        closing: "Nous sommes impatients de vous accueillir demain. Si vous avez besoin d'assistance supplémentaire, nous restons à votre disposition.",
        signature: "Cordialement,\nMaster Ski Pilou"
      }
    },

    // SURCLASSEMENT (isUpgrade = true)
    upgrade_with_address: {
      english: {
        subject: "Your Vessel Upgrade – {date}",
        greeting: "Dear {clientName},",
        intro: "We are delighted to inform you that your upcoming crossing {route} has been elevated to a larger and more spacious vessel, ensuring an even more refined and comfortable experience.",
        meetingPoint: "📍 Meeting Point: 14 Rue Jeanne d'Arc, Gustavia",
        time: "🕒 Time: {time}",
        date: "📅 Date: {date}",
        detailsTitle: "Your reservation details remain unchanged:",
        service: "• Service: {serviceType}",
        guests: "• Number of Guests: {numberOfGuests}",
        team: "Our team will be awaiting your arrival to provide a seamless and elegant boarding experience.",
        closing: "Should you need anything prior to departure, please do not hesitate to reach out.",
        signature: "Warm regards,\nMaster Ski Pilou"
      },
      french: {
        subject: "Amélioration de votre embarcation – {date}",
        greeting: "Cher/Chère {clientName},",
        intro: "Nous sommes ravis de vous informer que votre traversée {route} à venir a été améliorée vers un bateau plus grand et spacieux, garantissant une expérience encore plus raffinée et confortable.",
        meetingPoint: "📍 Point de rendez-vous : 14 Rue Jeanne d'Arc, Gustavia",
        time: "🕒 Heure : {time}",
        date: "📅 Date : {date}",
        detailsTitle: "Les détails de votre réservation restent inchangés :",
        service: "• Service : {serviceType}",
        guests: "• Nombre de passagers : {numberOfGuests}",
        team: "Notre équipe vous attendra pour vous offrir un embarquement fluide et élégant.",
        closing: "Si vous avez besoin de quoi que ce soit avant le départ, n'hésitez pas à nous contacter.",
        signature: "Cordialement,\nMaster Ski Pilou"
      }
    },
    upgrade_without_address: {
      english: {
        subject: "Your Vessel Upgrade – {date}",
        greeting: "Dear {clientName},",
        intro: "We are pleased to inform you that your upcoming crossing {route} will now take place on a larger and more comfortable vessel, offering you an enhanced journey.",
        time: "🕒 Time: {time}",
        date: "📅 Date: {date}",
        detailsTitle: "Your booking remains as originally arranged:",
        service: "• Service: {serviceType}",
        guests: "• Number of Guests: {numberOfGuests}",
        team: "Our crew will welcome you directly at the designated departure point for a smooth and effortless embarkation.",
        closing: "If you require any further assistance, we remain at your disposal.",
        signature: "Warm regards,\nMaster Ski Pilou"
      },
      french: {
        subject: "Amélioration de votre embarcation – {date}",
        greeting: "Cher/Chère {clientName},",
        intro: "Nous sommes heureux de vous informer que votre traversée {route} à venir se déroulera sur un bateau plus grand et confortable, vous offrant un voyage amélioré.",
        time: "🕒 Heure : {time}",
        date: "📅 Date : {date}",
        detailsTitle: "Votre réservation reste telle que prévue initialement :",
        service: "• Service : {serviceType}",
        guests: "• Nombre de passagers : {numberOfGuests}",
        team: "Notre équipage vous accueillera directement au point de départ pour un embarquement fluide.",
        closing: "Si vous avez besoin d'assistance supplémentaire, nous restons à votre disposition.",
        signature: "Cordialement,\nMaster Ski Pilou"
      }
    },
    // CONFIRMATION AGENT
    agent_confirmation_with_address: {
      english: {
        subject: "Booking Confirmation for Your Clients – {date}",
        greeting: "Dear {agentName},",
        intro: "We are pleased to confirm the booking for your clients {clientName} for tomorrow.",
        date: "📅 Date: {date}",
        time: "🕒 Time: {time}",
        service: "Service: {serviceType}",
        guests: "Guests: {numberOfGuests}",
        route: "Route: {route}",
        meetingInfo: "We will greet your clients at 14 Rue Jeanne d'Arc, Gustavia.",
        closing: "We look forward to welcoming them tomorrow. Please feel free to let us know if you need any further arrangements on their behalf.",
        signature: "Warm regards,\nMaster Ski Pilou"
      },
      french: {
        subject: "Confirmation de réservation pour vos clients – {date}",
        greeting: "Cher/Chère {agentName},",
        intro: "Nous sommes heureux de confirmer la réservation pour vos clients {clientName} pour demain.",
        date: "📅 Date : {date}",
        time: "🕒 Heure : {time}",
        service: "Service : {serviceType}",
        guests: "Passagers : {numberOfGuests}",
        route: "Trajet : {route}",
        meetingInfo: "Nous accueillerons vos clients au 14 Rue Jeanne d'Arc, Gustavia.",
        closing: "Nous sommes impatients de les accueillir demain. N'hésitez pas à nous faire savoir si vous avez besoin d'arrangements supplémentaires pour eux.",
        signature: "Cordialement,\nMaster Ski Pilou"
      }
    },
    agent_confirmation_without_address: {
      english: {
        subject: "Booking Confirmation for Your Clients – {date}",
        greeting: "Dear {agentName},",
        intro: "We are pleased to confirm the booking for your clients {clientName} for tomorrow.",
        date: "📅 Date: {date}",
        time: "🕒 Time: {time}",
        service: "Service: {serviceType}",
        guests: "Guests: {numberOfGuests}",
        route: "Route: {route}",
        closing: "We look forward to welcoming them tomorrow. Please feel free to let us know if you need any further arrangements on their behalf.",
        signature: "Warm regards,\nMaster Ski Pilou"
      },
      french: {
        subject: "Confirmation de réservation pour vos clients – {date}",
        greeting: "Cher/Chère {agentName},",
        intro: "Nous sommes heureux de confirmer la réservation pour vos clients {clientName} pour demain.",
        date: "📅 Date : {date}",
        time: "🕒 Heure : {time}",
        service: "Service : {serviceType}",
        guests: "Passagers : {numberOfGuests}",
        route: "Trajet : {route}",
        closing: "Nous sommes impatients de les accueillir demain. N'hésitez pas à nous faire savoir si vous avez besoin d'arrangements supplémentaires pour eux.",
        signature: "Cordialement,\nMaster Ski Pilou"
      }
    },

    // SURCLASSEMENT AGENT
    agent_upgrade_with_address: {
      english: {
        subject: "Vessel Upgrade Confirmed for Your Clients – {date}",
        greeting: "Dear {agentName},",
        intro: "We are pleased to advise that your clients {clientName} have been upgraded to a larger vessel for their crossing {route}, further enhancing the comfort of their experience.",
        date: "📅 Date: {date}",
        time: "🕒 Time: {time}",
        service: "Service: {serviceType}",
        guests: "Guests: {numberOfGuests}",
        meetingInfo: "We will greet your clients at 14 Rue Jeanne d'Arc, Gustavia.",
        closing: "Please feel free to let us know if you need any further arrangements on their behalf.",
        signature: "Warm regards,\nMaster Ski Pilou"
      },
      french: {
        subject: "Amélioration confirmée pour vos clients – {date}",
        greeting: "Cher/Chère {agentName},",
        intro: "Nous sommes heureux de vous informer que vos clients {clientName} ont été transférés vers un bateau plus grand pour leur traversée {route}, améliorant ainsi le confort de leur expérience.",
        date: "📅 Date : {date}",
        time: "🕒 Heure : {time}",
        service: "Service : {serviceType}",
        guests: "Passagers : {numberOfGuests}",
        meetingInfo: "Nous accueillerons vos clients au 14 Rue Jeanne d'Arc, Gustavia.",
        closing: "N'hésitez pas à nous faire savoir si vous avez besoin d'arrangements supplémentaires pour eux.",
        signature: "Cordialement,\nMaster Ski Pilou"
      }
    },
    agent_upgrade_without_address: {
      english: {
        subject: "Vessel Upgrade Confirmed for Your Clients – {date}",
        greeting: "Dear {agentName},",
        intro: "We are pleased to advise that your clients {clientName} have been upgraded to a larger vessel for their crossing {route}, further enhancing the comfort of their experience.",
        date: "📅 Date: {date}",
        time: "🕒 Time: {time}",
        service: "Service: {serviceType}",
        guests: "Guests: {numberOfGuests}",
        closing: "Please feel free to let us know if you need any further arrangements on their behalf.",
        signature: "Warm regards,\nMaster Ski Pilou"
      },
      french: {
        subject: "Amélioration confirmée pour vos clients – {date}",
        greeting: "Cher/Chère {agentName},",
        intro: "Nous sommes heureux de vous informer que vos clients {clientName} ont été transférés vers un bateau plus grand pour leur traversée {route}, améliorant ainsi le confort de leur expérience.",
        date: "📅 Date : {date}",
        time: "🕒 Heure : {time}",
        service: "Service : {serviceType}",
        guests: "Passagers : {numberOfGuests}",
        closing: "N'hésitez pas à nous faire savoir si vous avez besoin d'arrangements supplémentaires pour eux.",
        signature: "Cordialement,\nMaster Ski Pilou"
      }
    }
  };

  const getTemplateKey = () => {
    const showAddress = shouldShowAddress();
    const prefix = formData.isUpgrade ? 'upgrade' : 'confirmation';
    const recipientPrefix = formData.recipientType === 'agent' ? 'agent_' : '';
    const addressSuffix = showAddress ? '_with_address' : '_without_address';

    return `${recipientPrefix}${prefix}${addressSuffix}`;
  };

  const generateEmailContent = () => {
    const templateKey = getTemplateKey();
    const template = emailTemplates[templateKey][formData.language];
    const route = getRoute();
    const time = getTime();

    const replacePlaceholders = (text) => {
      return text
        .replace(/{clientName}/g, formData.clientName || '[Client Name]')
        .replace(/{agentName}/g, formData.agentName || '[Agent Name]')
        .replace(/{route}/g, route)
        .replace(/{date}/g, formData.date || '[Date]')
        .replace(/{time}/g, time || '[Time]')
        .replace(/{serviceType}/g, formData.serviceType)
        .replace(/{numberOfGuests}/g, formData.numberOfGuests);
    };

    // Add replacements for vessel placeholders
    // Use shared getVesselLabel (declared above) to build labels
    const replacePlaceholdersWithVessels = (text) => {
      const newLabel = getVesselLabel(formData.newVessel);
      return replacePlaceholders(text)
        .replace(/{newVessel}/g, newLabel || '[New Vessel]');
    };

    let textContent = '';
    let htmlContent = '';

    // Prepare upgrade lines (plain text and html) if this is an upgrade
    // Use language-aware short phrasing that only mentions the NEW vessel
    // Different wording for client vs agent
    let upgradePlainTemplate, upgradeHtmlTemplate;

    if (formData.recipientType === 'agent') {
      // Pour les agents: "votre client va être surclassé"
      upgradePlainTemplate = formData.language === 'french'
        ? 'Votre client va être surclassé vers {newVessel}.'
        : "Your client will be upgraded to {newVessel}.";
      upgradeHtmlTemplate = formData.language === 'french'
        ? '<strong>Votre client va être surclassé vers {newVessel}.</strong>'
        : "<strong>Your client will be upgraded to {newVessel}.</strong>";
    } else {
      // Pour les clients directs: "vous avez été surclassé"
      upgradePlainTemplate = formData.language === 'french'
        ? 'Vous avez été surclassé vers {newVessel}.'
        : "You've been upgraded to {newVessel}.";
      upgradeHtmlTemplate = formData.language === 'french'
        ? '<strong>Vous avez été surclassé vers {newVessel}.</strong>'
        : "<strong>You've been upgraded to {newVessel}.</strong>";
    }

    const upgradeText = formData.isUpgrade
      ? replacePlaceholdersWithVessels(upgradePlainTemplate)
      : '';

    const upgradeHtml = formData.isUpgrade
      ? replacePlaceholdersWithVessels(upgradeHtmlTemplate)
      : '';

    if (formData.recipientType === 'agent') {
      // Agent email
      if (shouldShowAddress()) {
        // AVEC adresse
  textContent = `${replacePlaceholders(template.greeting)}

${replacePlaceholders(template.intro)}

${replacePlaceholders(template.date)}
${replacePlaceholders(template.time)}
${replacePlaceholders(template.service)}
${replacePlaceholders(template.guests)}

${upgradeText ? upgradeText + '\n' : ''}
${template.meetingInfo}

${template.closing}

${template.signature}`;

  htmlContent = `${replacePlaceholders(template.greeting)}<br/><br/>
${replacePlaceholders(template.intro)}<br/><br/>
<strong>${replacePlaceholders(template.date)}</strong><br/>
<strong>${replacePlaceholders(template.time)}</strong><br/>
<strong>${replacePlaceholders(template.service)}</strong><br/>
<strong>${replacePlaceholders(template.guests)}</strong><br/><br/>
${upgradeHtml ? upgradeHtml + '<br/>' : ''}
${template.meetingInfo}<br/><br/>
${template.closing}<br/><br/>
${template.signature.replace(/\n/g, '<br/>')}`;
      } else {
        // SANS adresse
  textContent = `${replacePlaceholders(template.greeting)}

${replacePlaceholders(template.intro)}

${replacePlaceholders(template.date)}
${replacePlaceholders(template.time)}
${replacePlaceholders(template.service)}
${replacePlaceholders(template.guests)}

${upgradeText ? upgradeText + '\n' : ''}
${template.closing}

${template.signature}`;

  htmlContent = `${replacePlaceholders(template.greeting)}<br/><br/>
${replacePlaceholders(template.intro)}<br/><br/>
<strong>${replacePlaceholders(template.date)}</strong><br/>
<strong>${replacePlaceholders(template.time)}</strong><br/>
<strong>${replacePlaceholders(template.service)}</strong><br/>
<strong>${replacePlaceholders(template.guests)}</strong><br/><br/>
${upgradeHtml ? upgradeHtml + '<br/>' : ''}
${template.closing}<br/><br/>
${template.signature.replace(/\n/g, '<br/>')}`;
      }

    } else {
      // Client email
      if (shouldShowAddress()) {
        // AVEC adresse (départ de SBH OU Private Charter)
        const routeLine = template.route ? `${replacePlaceholders(template.route)}` : '';

  textContent = `${replacePlaceholders(template.greeting)}

${replacePlaceholders(template.intro)}

${template.meetingPoint}
${replacePlaceholders(template.time)}
${replacePlaceholders(template.date)}

${template.detailsTitle}
${replacePlaceholders(template.service)}
${replacePlaceholders(template.guests)}
${routeLine}

${upgradeText ? upgradeText + '\n' : ''}
${template.team}

${template.childrenNote}

${template.closing}

${template.signature}`;

  htmlContent = `${replacePlaceholders(template.greeting)}<br/><br/>
${replacePlaceholders(template.intro)}<br/><br/>
<strong>${template.meetingPoint}</strong><br/>
<strong>${replacePlaceholders(template.time)}</strong><br/>
<strong>${replacePlaceholders(template.date)}</strong><br/><br/>
${template.detailsTitle}<br/>
${replacePlaceholders(template.service)}<br/>
${replacePlaceholders(template.guests)}<br/>
${routeLine ? routeLine + '<br/>' : ''}
${upgradeHtml ? upgradeHtml + '<br/>' : ''}
${template.team}<br/><br/>
<em>${template.childrenNote}</em><br/><br/>
${template.closing}<br/><br/>
${template.signature.replace(/\n/g, '<br/>')}`;

      } else {
        // SANS adresse (départ autre île ET Transfer)
        const routeLine = template.route ? `${replacePlaceholders(template.route)}` : '';

  textContent = `${replacePlaceholders(template.greeting)}

${replacePlaceholders(template.intro)}

${replacePlaceholders(template.time)}
${replacePlaceholders(template.date)}

${template.detailsTitle}
${replacePlaceholders(template.service)}
${replacePlaceholders(template.guests)}
${routeLine}

${upgradeText ? upgradeText + '\n' : ''}
${template.team}

${template.childrenNote}

${template.closing}

${template.signature}`;

  htmlContent = `${replacePlaceholders(template.greeting)}<br/><br/>
${replacePlaceholders(template.intro)}<br/><br/>
<strong>${replacePlaceholders(template.time)}</strong><br/>
<strong>${replacePlaceholders(template.date)}</strong><br/><br/>
${template.detailsTitle}<br/>
${replacePlaceholders(template.service)}<br/>
${replacePlaceholders(template.guests)}<br/>
${routeLine ? routeLine + '<br/>' : ''}
${upgradeHtml ? upgradeHtml + '<br/>' : ''}
${template.team}<br/><br/>
<em>${template.childrenNote}</em><br/><br/>
${template.closing}<br/><br/>
${template.signature.replace(/\n/g, '<br/>')}`;
      }
    }

    const subject = replacePlaceholders(template.subject);

    return { textContent, htmlContent, subject };
  };

  const addToQueue = () => {
    // Validate minimal fields (same as sendEmail)
    if (!formData.email) {
      alert('Veuillez saisir une adresse email / Please enter an email address');
      return;
    }

    if (!formData.clientName && formData.recipientType === 'client') {
      alert('Veuillez saisir le nom du client / Please enter client name');
      return;
    }

    if (!formData.agentName && formData.recipientType === 'agent') {
      alert('Veuillez saisir le nom de l\'agent / Please enter agent name');
      return;
    }

    if (!formData.date) {
      alert('Veuillez saisir une date / Please enter a date');
      return;
    }

    const time = getTime();
    if (!time) {
      alert('Veuillez saisir une heure / Please enter a time');
      return;
    }

    if (formData.departureFrom === formData.destinationTo) {
      alert('Le départ et la destination ne peuvent pas être identiques / Departure and destination cannot be the same');
      return;
    }

    const { textContent, htmlContent, subject } = generateEmailContent();

    const newVesselLabel = getVesselLabel(formData.newVessel);

    const queuedItem = {
      id: Date.now() + Math.random(),
      to: formData.email,
      subject,
      textContent,
      htmlContent,
      formSnapshot: { ...formData },
      newVesselLabel
    };

    setQueuedEmails(prev => [...prev, queuedItem]);

    // Reset form for next entry but keep recipientType and language
    setFormData({
      recipientType: formData.recipientType,
      departureFrom: 'SBH',
      destinationTo: 'SXM',
      clientName: '',
      agentName: '',
      email: '',
      date: getTomorrowDate(),
      timeType: 'preset',
      presetTime: '09:00',
      customTime: '',
      serviceType: 'Transfer',
      numberOfGuests: '2',
      language: formData.language,
      isUpgrade: true,
      newVessel: ''
    });
  };

  const removeQueuedItem = (id) => {
    setQueuedEmails(prev => prev.filter(i => i.id !== id));
  };

  const sendAllQueued = async () => {
    if (queuedEmails.length === 0) return;
    setIsSendingQueue(true);
    setQueueProgress({ sent: 0, total: queuedEmails.length });

    const results = [];
    for (let i = 0; i < queuedEmails.length; i++) {
      const item = queuedEmails[i];
      // Build webhook payload from snapshot
      const snap = item.formSnapshot;
      const route = `${snap.departureFrom}/${snap.destinationTo}`;
      const time = snap.timeType === 'preset' ? snap.presetTime : snap.customTime;
      const payload = {
        to: item.to,
        subject: item.subject,
        content: item.textContent,
        htmlContent: item.htmlContent,
        recipientType: snap.recipientType,
        departureFrom: snap.serviceType === 'Private Charter' ? 'SBH' : snap.departureFrom,
        destinationTo: snap.serviceType === 'Private Charter' ? 'SBH' : snap.destinationTo,
        route,
        clientName: snap.clientName,
        agentName: snap.agentName,
        date: snap.date,
        time,
        timeType: snap.timeType,
        presetTime: snap.presetTime,
        customTime: snap.customTime,
        serviceType: snap.serviceType,
        numberOfGuests: snap.numberOfGuests,
        language: snap.language,
        isUpgrade: snap.isUpgrade,
        newVessel: snap.newVessel,
        newVesselLabel: item.newVesselLabel,
        showAddress: snap.serviceType === 'Private Charter' ? true : (snap.departureFrom === 'SBH')
      };

      try {
        const resp = await fetch(MAKE_WEBHOOK_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        results.push(resp.ok);
      } catch (err) {
        console.error('Error sending queued item', err);
        results.push(false);
      }

      setQueueProgress(prev => ({ ...prev, sent: prev.sent + 1 }));
    }

    setIsSendingQueue(false);

    const successCount = results.filter(r => r).length;
    if (successCount === queuedEmails.length) {
      alert(`✅ Tous les ${successCount} emails ont été envoyés avec succès`);
      setQueuedEmails([]);
    } else {
      alert(`⚠️ Envoi ${successCount}/${queuedEmails.length}. Vérifiez les logs pour les erreurs.`);
      // For simplicity clear queue after attempt
      setQueuedEmails([]);
    }
  };

  const handleInputChange = (field, value) => {
    const newFormData = { ...formData, [field]: value };
    
    // Si on change le départ, on ajuste automatiquement la destination
    if (field === 'departureFrom') {
      if (value === 'SBH') {
        // Si départ SBH, destination par défaut SXM
        newFormData.destinationTo = newFormData.destinationTo === 'SBH' ? 'SXM' : newFormData.destinationTo;
      } else if (value === 'SXM') {
        // Si départ SXM, destination par défaut SBH
        newFormData.destinationTo = newFormData.destinationTo === 'SXM' ? 'SBH' : newFormData.destinationTo;
      } else if (value === 'AXA') {
        // Si départ AXA, destination par défaut SBH
        newFormData.destinationTo = newFormData.destinationTo === 'AXA' ? 'SBH' : newFormData.destinationTo;
      }
    }
    
    setFormData(newFormData);
  };

  const updateEmailPreview = () => {
    const { textContent, htmlContent } = generateEmailContent();
    setEmailPreview(textContent);
    setHtmlEmailPreview(htmlContent);
  };

  React.useEffect(() => {
    updateEmailPreview();
  }, [formData]);

  const sendEmail = async () => {
    if (!formData.email) {
      alert('Veuillez saisir une adresse email / Please enter an email address');
      return;
    }

    if (!formData.clientName && formData.recipientType === 'client') {
      alert('Veuillez saisir le nom du client / Please enter client name');
      return;
    }

    if (!formData.agentName && formData.recipientType === 'agent') {
      alert('Veuillez saisir le nom de l\'agent / Please enter agent name');
      return;
    }

    if (!formData.date) {
      alert('Veuillez saisir une date / Please enter a date');
      return;
    }

    // Validate time based on timeType
    const time = getTime();
    if (!time) {
      alert('Veuillez saisir une heure / Please enter a time');
      return;
    }

    if (formData.departureFrom === formData.destinationTo) {
      alert('Le départ et la destination ne peuvent pas être identiques / Departure and destination cannot be the same');
      return;
    }

    setIsLoading(true);
    
    try {
      const { textContent, htmlContent, subject } = generateEmailContent();
      const route = getRoute();
      const time = getTime();

      // Build safe vessel labels for webhook (same logic as in generateEmailContent)
      const findVesselLabel = (value) => {
        if (!value) return '';
        const found = yachts.find(y => String(y.id) === String(value));
        if (found) {
          const size = found.length_feet ? `${found.length_feet}ft` : (found.length_meters ? `${found.length_meters}m` : '');
          const brandOrName = found.brand || found.name || 'Vessel';
          return size ? `${brandOrName} (${size})` : brandOrName;
        }
        return value;
      };

      const currentVesselLabel = findVesselLabel(formData.currentVessel);
      const newVesselLabel = findVesselLabel(formData.newVessel);

      const webhookData = {
        to: formData.email,
        subject: subject,
        content: textContent,
        htmlContent: htmlContent,
        recipientType: formData.recipientType,
        departureFrom: formData.serviceType === 'Private Charter' ? 'SBH' : formData.departureFrom,
        destinationTo: formData.serviceType === 'Private Charter' ? 'SBH' : formData.destinationTo,
        route: route,
        clientName: formData.clientName,
        agentName: formData.agentName,
        date: formData.date,
        time: time,
        timeType: formData.timeType,
        presetTime: formData.presetTime,
        customTime: formData.customTime,
        serviceType: formData.serviceType,
        numberOfGuests: formData.numberOfGuests,
        language: formData.language,
        isUpgrade: formData.isUpgrade,
        currentVessel: formData.currentVessel,
        newVessel: formData.newVessel,
        currentVesselLabel: currentVesselLabel,
        newVesselLabel: newVesselLabel,
        showAddress: shouldShowAddress()
      };

      // ⚠️ VOTRE URL WEBHOOK MAKE ICI
      const makeWebhookUrl = 'https://hook.eu2.make.com/ziechx2b1lutse6wcmrliwag4blynr2r';
      
      const response = await fetch(makeWebhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(webhookData)
      });
      
      if (response.ok) {
        alert('✅ Email envoyé avec succès !');
        // Reset form avec date de demain
        setFormData({
          recipientType: 'client',
          departureFrom: 'SBH',
          destinationTo: 'SXM',
          clientName: '',
          agentName: '',
          email: '',
          date: getTomorrowDate(),
          timeType: 'preset',
          presetTime: '09:00',
          customTime: '',
          serviceType: 'Transfer',
          numberOfGuests: '2',
          language: 'english',
          isUpgrade: false,
          currentVessel: '',
          newVessel: ''
        });
      } else {
        throw new Error('Failed to send email');
      }
      
    } catch (error) {
      console.error('Error sending email:', error);
      alert("❌ Erreur lors de l'envoi");
    } finally {
      setIsLoading(false);
    }
  };

  // Styles Uber
  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: theme.colors.glassLight,
      padding: '20px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    mainWrapper: {
      maxWidth: '1200px',
      margin: '0 auto'
    },
    header: {
      backgroundColor: '#000000',
      color: 'white',
      padding: '30px',
      borderRadius: '8px',
      marginBottom: '30px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
    },
    headerTitle: {
      fontSize: '32px',
      fontWeight: '700',
      display: 'flex',
      alignItems: 'center',
      gap: '15px',
      margin: 0
    },
    headerSubtitle: {
      margin: '10px 0 0 0',
      opacity: 0.8,
      fontSize: '16px'
    },
    gridContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))',
      gap: '24px'
    },
    card: {
      backgroundColor: theme.colors.glassMedium,
      padding: '32px',
      borderRadius: '8px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      border: '1px solid #e5e5e5'
    },
    cardTitle: {
      fontSize: '24px',
      fontWeight: '600',
      marginBottom: '24px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      color: '#111'
    },
    formGroup: {
      marginBottom: '20px'
    },
    label: {
      display: 'block',
      fontSize: '14px',
      fontWeight: '500',
      color: theme.colors.textPrimary,
      marginBottom: '8px'
    },
    input: {
      width: '100%',
      padding: '12px 16px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      fontSize: '16px',
      transition: 'border-color 0.2s ease',
      outline: 'none',
      boxSizing: 'border-box',
      backgroundColor: theme.colors.glassMedium
    },
    select: {
      width: '100%',
      padding: '12px 16px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      fontSize: '16px',
      backgroundColor: theme.colors.glassMedium,
      cursor: 'pointer',
      outline: 'none',
      boxSizing: 'border-box'
    },
    button: {
      width: '100%',
      padding: '16px 24px',
      backgroundColor: '#000000',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      fontSize: '16px',
      fontWeight: '500',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      transition: 'background-color 0.2s ease',
      outline: 'none'
    },
    buttonDisabled: {
      backgroundColor: '#ccc',
      cursor: 'not-allowed'
    },
    previewBox: {
      backgroundColor: theme.colors.glassLight,
      padding: '20px',
      border: '1px solid #e5e5e5',
      borderRadius: '4px',
      minHeight: '200px'
    },
    preText: {
      whiteSpace: 'pre-wrap',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      lineHeight: '1.6',
      color: theme.colors.textPrimary,
      margin: 0
    },
    instructionsBox: {
      backgroundColor: '#f8f8f8',
      border: '1px solid #e5e5e5',
      padding: '20px',
      borderRadius: '4px',
      marginTop: '24px'
    },
    instructionsList: {
      margin: '10px 0',
      paddingLeft: '20px',
      color: '#555',
      fontSize: '14px'
    },
    routeDisplay: {
      padding: '16px',
      backgroundColor: '#f0f9ff',
      border: '2px solid #0284c7',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '12px',
      fontSize: '20px',
      fontWeight: 'bold',
      color: '#0c4a6e',
      marginBottom: '20px'
    },
    addressIndicator: {
      padding: '12px',
      backgroundColor: '#dcfce7',
      border: '2px solid #16a34a',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      fontSize: '14px',
      fontWeight: 'bold',
      color: '#166534',
      marginBottom: '20px'
    },
    checkboxContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '16px',
      backgroundColor: '#fef3c7',
      border: '2px solid #f59e0b',
      borderRadius: '8px',
      marginBottom: '20px',
      cursor: 'pointer',
      transition: 'all 0.2s ease'
    },
    checkboxContainerActive: {
      backgroundColor: '#dcfce7',
      borderColor: '#16a34a'
    },
    checkbox: {
      width: '24px',
      height: '24px',
      cursor: 'pointer'
    },
    checkboxLabel: {
      fontSize: '15px',
      fontWeight: '600',
      color: theme.colors.textPrimary,
      cursor: 'pointer',
      userSelect: 'none'
    },
    timeTypeToggle: {
      display: 'flex',
      gap: '12px',
      marginBottom: '12px'
    },
    timeTypeButton: {
      flex: 1,
      padding: '10px 16px',
      border: '2px solid #ddd',
      borderRadius: '6px',
      backgroundColor: theme.colors.glassMedium,
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      outline: 'none'
    },
    timeTypeButtonActive: {
      backgroundColor: '#000',
      color: 'white',
      borderColor: '#000'
    }
  };

  // Fonction pour obtenir les destinations disponibles selon le départ
  const getAvailableDestinations = () => {
    const all = ['SBH', 'SXM', 'AXA'];
    return all.filter(dest => dest !== formData.departureFrom);
  };

  return (
    <div style={{ minHeight: '100vh', background: theme.colors.bgDark, padding: '20px' }} style={styles.container}>
      <div style={styles.mainWrapper}>
        {/* Header */}
        <div style={styles.header}>
          <h1 style={styles.headerTitle}>
            <Ship size={40} />
            Envoi d'emails - Confirmation et Surclassement
          </h1>
          <p style={styles.headerSubtitle}>
            Envoi d'emails de confirmation ou de surclassement
          </p>
          {loadingYachts && (
            <p style={{fontSize: '12px', marginTop: '8px', opacity: 0.7}}>
              Chargement des bateaux...
            </p>
          )}
        </div>

        <div style={styles.gridContainer}>
          {/* Configuration Panel */}
          <div>
            <div style={styles.card}>
              <h2 style={styles.cardTitle}>
                <Ship size={24} />
                Configuration
              </h2>
              
              {/* Type + Nom sur même ligne */}
              <div style={{display: 'grid', gridTemplateColumns: '140px 1fr', gap: '12px', marginBottom: '16px'}}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Type</label>
                  <select
                    value={formData.recipientType}
                    onChange={(e) => handleInputChange('recipientType', e.target.value)}
                    style={styles.select}
                  >
                    <option value="client">Client</option>
                    <option value="agent">Agence</option>
                  </select>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    {formData.recipientType === 'client' ? 'Nom du client' : 'Nom de l\'agent'}
                  </label>
                  <input
                    type="text"
                    value={formData.recipientType === 'client' ? formData.clientName : formData.agentName}
                    onChange={(e) => handleInputChange(formData.recipientType === 'client' ? 'clientName' : 'agentName', e.target.value)}
                    placeholder={formData.recipientType === 'client' ? 'John Smith' : 'Agency Name'}
                    style={styles.input}
                  />
                </div>
              </div>

              {/* Nom du client si agent */}
              {formData.recipientType === 'agent' && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>Nom du client</label>
                  <input
                    type="text"
                    value={formData.clientName}
                    onChange={(e) => handleInputChange('clientName', e.target.value)}
                    placeholder="John Smith"
                    style={styles.input}
                  />
                </div>
              )}

              {/* Email + Langue sur même ligne */}
              <div style={{display: 'grid', gridTemplateColumns: '1fr 120px', gap: '12px', marginBottom: '16px'}}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="email@example.com"
                    style={styles.input}
                  />
                </div>

                <div style={styles.formGroup}>
                    <label style={styles.label}>Langue</label>
                  <select
                    value={formData.language}
                    onChange={(e) => handleInputChange('language', e.target.value)}
                    style={styles.select}
                  >
                    <option value="english">EN</option>
                    <option value="french">FR</option>
                  </select>
                </div>
              </div>

              {/* Service Type */}
              <div style={styles.formGroup}>
                    <label style={styles.label}>Type de service</label>
                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px'}}>
                  <button
                    type="button"
                    onClick={() => handleInputChange('serviceType', 'Transfer')}
                    style={{
                      ...styles.timeTypeButton,
                      ...(formData.serviceType === 'Transfer' ? styles.timeTypeButtonActive : {}),
                      padding: '10px'
                    }}
                  >
                        Transfert
                  </button>
                  <button
                    type="button"
                    onClick={() => handleInputChange('serviceType', 'Private Charter')}
                    style={{
                      ...styles.timeTypeButton,
                      ...(formData.serviceType === 'Private Charter' ? styles.timeTypeButtonActive : {}),
                      padding: '10px'
                    }}
                  >
                        Location privée
                  </button>
                </div>
              </div>

              {/* Checkbox Surclassement */}
              <div style={styles.formGroup}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '12px',
                    backgroundColor: '#f9fafb',
                    border: '1px solid #e5e7eb',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                  onClick={() => handleInputChange('isUpgrade', !formData.isUpgrade)}
                >
                  <input
                    type="checkbox"
                    checked={formData.isUpgrade}
                    onChange={(e) => handleInputChange('isUpgrade', e.target.checked)}
                    style={styles.checkbox}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <label style={{fontSize: '14px', fontWeight: '500', color: '#374151', cursor: 'pointer'}}>
                    Surclassement (vers un bateau plus grand)
                  </label>
                </div>
              </div>

              {/* Vessel Selection - Si Upgrade (only new vessel) */}
              {formData.isUpgrade && (
                <div style={{display: 'grid', gridTemplateColumns: '1fr', gap: '12px', marginBottom: '16px'}}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Nouveau bateau</label>
                    <select
                      value={formData.newVessel}
                      onChange={(e) => handleInputChange('newVessel', e.target.value)}
                      style={styles.select}
                    >
                      <option value="">--</option>
                      {yachts.map(yacht => (
                        <option key={yacht.id} value={yacht.id}>
                          {yacht.brand} ({yacht.length_feet}ft)
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* Departure/Destination SEULEMENT pour Transfer */}
              {formData.serviceType === 'Transfer' && (
                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px'}}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Départ</label>
                    <select
                      value={formData.departureFrom}
                      onChange={(e) => handleInputChange('departureFrom', e.target.value)}
                      style={styles.select}
                    >
                      <option value="SBH">SBH</option>
                      <option value="SXM">SXM</option>
                      <option value="AXA">AXA</option>
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Destination</label>
                    <select
                      value={formData.destinationTo}
                      onChange={(e) => handleInputChange('destinationTo', e.target.value)}
                      style={styles.select}
                    >
                      {getAvailableDestinations().map(dest => (
                        <option key={dest} value={dest}>{dest}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* Date + Time + Guests */}
              <div style={{display: 'grid', gridTemplateColumns: '1fr 100px 80px', gap: '12px', marginBottom: '16px'}}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Date</label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => handleInputChange('date', e.target.value)}
                    style={styles.input}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Heure</label>
                  {formData.timeType === 'preset' ? (
                    <select
                      value={formData.presetTime}
                      onChange={(e) => handleInputChange('presetTime', e.target.value)}
                      style={styles.select}
                    >
                      <option value="09:00">09:00</option>
                      <option value="13:00">13:00</option>
                      <option value="17:00">17:00</option>
                    </select>
                  ) : (
                    <input
                      type="time"
                      value={formData.customTime}
                      onChange={(e) => handleInputChange('customTime', e.target.value)}
                      style={styles.input}
                    />
                  )}
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Passagers</label>
                  <input
                    type="number"
                    value={formData.numberOfGuests}
                    onChange={(e) => handleInputChange('numberOfGuests', e.target.value)}
                    min="1"
                    style={styles.input}
                  />
                </div>
              </div>

              {/* Toggle Time Type */}
              <div style={{...styles.timeTypeToggle, marginTop: '-12px', marginBottom: '16px'}}>
                <button
                  type="button"
                  onClick={() => handleInputChange('timeType', 'preset')}
                  style={{
                    ...styles.timeTypeButton,
                    ...(formData.timeType === 'preset' ? styles.timeTypeButtonActive : {}),
                    padding: '6px 12px',
                    fontSize: '12px'
                  }}
                >
                  Préréglé
                </button>
                <button
                  type="button"
                  onClick={() => handleInputChange('timeType', 'custom')}
                  style={{
                    ...styles.timeTypeButton,
                    ...(formData.timeType === 'custom' ? styles.timeTypeButtonActive : {}),
                    padding: '6px 12px',
                    fontSize: '12px'
                  }}
                >
                  Personnalisé
                </button>
              </div>

              {/* Send Button */}
              <button
                onClick={sendEmail}
                disabled={isLoading || !formData.email}
                style={{
                  ...styles.button,
                  ...(isLoading || !formData.email ? styles.buttonDisabled : {})
                }}
              >
                    {isLoading ? (
                  <div style={{
                    width: '20px',
                    height: '20px',
                    border: '2px solid white',
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                ) : (
                  <>
                    <Send size={20} />
                        Envoyer l'email
                  </>
                )}
              </button>
              <div style={{display: 'flex', gap: '12px', marginTop: '12px'}}>
                <button
                  type="button"
                  onClick={addToQueue}
                  style={{
                    ...styles.button,
                    width: 'auto',
                    padding: '10px 16px'
                  }}
                >
                  ➕ Ajouter un autre
                </button>

                <button
                  type="button"
                  onClick={sendAllQueued}
                  disabled={isSendingQueue || queuedEmails.length === 0}
                  style={{
                    ...styles.button,
                    width: 'auto',
                    padding: '10px 16px',
                    backgroundColor: queuedEmails.length === 0 ? '#999' : '#0b74de'
                  }}
                >
                  {isSendingQueue ? `Envoi ${queueProgress.sent}/${queueProgress.total}` : `Envoyer tout (${queuedEmails.length})`}
                </button>
              </div>

              {/* Queued items summary */}
              {queuedEmails.length > 0 && (
                <div style={{marginTop: '12px', padding: '12px', backgroundColor: '#f8fafc', borderRadius: '6px', border: '1px solid #e6eef8'}}>
                  <strong>Emails en file d'attente :</strong>
                  <ul style={{marginTop: '8px'}}>
                    {queuedEmails.map(item => (
                      <li key={item.id} style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', padding: '6px 0'}}>
                        <span style={{fontSize: '13px'}}>{item.to} — {item.newVesselLabel} — {item.formSnapshot.clientName || item.formSnapshot.agentName}</span>
                        <button type="button" onClick={() => removeQueuedItem(item.id)} style={{background: 'transparent', border: 'none', color: '#c00', cursor: 'pointer'}}>Supprimer</button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* Email Preview */}
          <div>
            <div style={styles.card}>
                <h2 style={styles.cardTitle}>
                📧 Aperçu de l'email
              </h2>
              <div style={styles.previewBox}>
                <div style={{fontSize: '14px', color: '#666', marginBottom: '5px'}}>
                  <strong>À :</strong> {formData.email || 'email@example.com'}
                </div>
                <div style={{fontSize: '14px', color: '#666', marginBottom: '15px'}}>
                  <strong>Sujet :</strong> {generateEmailContent().subject}
                </div>
                <div style={{borderTop: '2px solid #ddd', paddingTop: '15px'}}>
                  <pre style={styles.preText}>
                    {emailPreview}
                  </pre>
                </div>
              </div>
            </div>

            {/* HTML Preview */}
            <div style={{...styles.card, marginTop: '20px'}}>
                <h2 style={{...styles.cardTitle, fontSize: '20px'}}>
                🎨 Aperçu HTML
              </h2>
              <div style={styles.previewBox}>
                <div 
                  style={{lineHeight: '1.6', color: theme.colors.textPrimary}}
                  dangerouslySetInnerHTML={{ __html: htmlEmailPreview }}
                />
              </div>
            </div>

            {/* Instructions Make */}
            <div style={styles.instructionsBox}>
              <h3 style={{fontWeight: 'bold', color: '#e65100', marginBottom: '10px'}}>
                ⚙️ Configuration Make
              </h3>
              <p style={{fontSize: '14px', color: '#bf360c', marginBottom: '10px'}}>
                <strong>Dans votre module "Gmail &gt; Send an Email" de Make :</strong>
              </p>
              <ul style={styles.instructionsList}>
                <li><strong>Connexion :</strong> Votre compte Gmail</li>
                <li><strong>À :</strong> {`{{1.to}}`}</li>
                <li><strong>Sujet :</strong> {`{{1.subject}}`}</li>
                <li><strong>Type de contenu :</strong> HTML ⚠️</li>
                <li><strong>Contenu :</strong> {`{{1.htmlContent}}`} ⚠️</li>
                <li><strong>Nom de l'expéditeur :</strong> Master Ski Pilou</li>
              </ul>
              <div style={{marginTop: '15px', padding: '12px', backgroundColor: '#fff3e0', borderRadius: '4px', border: '1px solid #ffb74d'}}>
                <strong style={{color: '#e65100'}}>✅ Webhook URL configurée :</strong>
                <div style={{fontSize: '12px', color: '#666', marginTop: '5px', wordBreak: 'break-all'}}>
                  https://hook.eu2.make.com/ziechx2b1lutse6wcmrliwag4blynr2r
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default VesselUpgradeEmailSender;